import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface FormData {
  first_name: string;
  last_name: string;
  username: string;
  password: string;
  phone: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { signup, error, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    username: '',
    password: '',
    phone: '',
  });
  const [formError, setFormError] = useState<string>('');

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateForm = (): boolean => {
    if (!formData.first_name || !formData.last_name || !formData.username || !formData.password || !formData.phone) {
      setFormError('All fields are required');
      return false;
    }
    if (formData.password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(formData.username)) {
      setFormError('Please enter a valid email address');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const result = await signup(formData);
    if (result.success) {
      alert('Account created successfully. Please login.');
      navigate('/login');
    } else {
      setFormError(result.message || 'Signup failed');
    }
  };

  return (
    <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8 bg-dark-200">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          צור חשבון חדש
        </h2>
      </div>

      <div className="mt-8 m-2 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-dark-300 py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-300">
                שם פרטי
              </label>
              <div className="mt-1">
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-300">
                שם משפחה
              </label>
              <div className="mt-1">
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300">
                כתובת אימייל
              </label>
              <div className="mt-1">
                <input
                  id="username"
                  name="username"
                  type="email"
                  autoComplete="email"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                מספר טלפון
              </label>
              <div className="mt-1">
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300">
                סיסמא
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 border border-stake-border rounded-md shadow-sm placeholder-gray-500 bg-dark-200 text-white focus:outline-none focus:ring-stake-blue focus:border-stake-blue sm:text-sm"
                />
              </div>
            </div>

            {(formError || error) && (
              <div className="text-stake-red text-sm">
                {formError || error}
              </div>
            )}

            <div>
              <button
                type="submit"
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-stake-blue hover:bg-stake-blue-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-stake-blue"
              >
                הירשם
              </button>
            </div>
          </form>

          <div className="mt-6">
            <div className="text-center text-sm">
              <Link 
                to="/login" 
                className="font-medium text-stake-blue hover:text-stake-blue-light"
              >
                כבר יש לך חשבון? התחבר
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 